/**
 * We compose our own spoken instructions instead of reading Mapbox's.
 *
 * Mapbox's `voiceInstructions` are well-timed but they splice OSM road names
 * straight into the sentence, so asking for English still yields
 * "Turn right onto ถนนมหาจักรพรรดิ์" - unreadable on the sign, and enough to
 * make Android TTS swap to a Thai voice mid-utterance. Composing the sentence
 * ourselves means every word is in the selected language, road numbers are
 * used where a name would be unreadable, and the lane warning can be spoken as
 * part of the same breath.
 */

import type { LaneAdvice } from './lanes';
import type { ForkInfo } from './forks';
import type { RouteStep } from '../core/types';
import type { UnitSystem } from '../core/settings';
import type { Lang } from '../core/i18n';
import { roadLabel } from './roads';

type Phrases = Record<Lang, string>;

const p = (en: string, th: string): Phrases => ({ en, th });

/** Verb for a maneuver, before any road is appended. */
function verb(step: RouteStep, lang: Lang): string {
  const m = step.maneuver;
  const type = (m.type ?? '').toLowerCase();
  const mod = (m.modifier ?? '').toLowerCase();
  const left = mod.includes('left');

  const pick = (ph: Phrases): string => ph[lang];

  switch (type) {
    case 'arrive':
      return mod === 'left'
        ? pick(p('Your destination is on the left', 'จุดหมายอยู่ทางซ้าย'))
        : mod === 'right'
          ? pick(p('Your destination is on the right', 'จุดหมายอยู่ทางขวา'))
          : pick(p('You have arrived', 'ถึงจุดหมายแล้ว'));

    case 'roundabout':
    case 'rotary':
    case 'roundabout turn':
      return m.exit
        ? pick(p(`At the roundabout, take exit ${m.exit}`, `ที่วงเวียน ออกทางที่ ${m.exit}`))
        : pick(p('At the roundabout', 'ที่วงเวียน'));

    case 'exit roundabout':
    case 'exit rotary':
      return pick(p('Leave the roundabout', 'ออกจากวงเวียน'));

    case 'off ramp':
      return left
        ? pick(p('Take the exit on the left', 'ออกทางแยกซ้าย'))
        : pick(p('Take the exit on the right', 'ออกทางแยกขวา'));

    case 'on ramp':
      return left
        ? pick(p('Take the ramp on the left', 'ขึ้นทางลาดซ้าย'))
        : pick(p('Take the ramp on the right', 'ขึ้นทางลาดขวา'));

    case 'merge':
      return left
        ? pick(p('Merge left', 'รวมช่องทางซ้าย'))
        : pick(p('Merge right', 'รวมช่องทางขวา'));

    case 'fork':
      return left
        ? pick(p('Keep left at the fork', 'ชิดซ้ายที่ทางแยก'))
        : pick(p('Keep right at the fork', 'ชิดขวาที่ทางแยก'));

    case 'end of road':
      return left
        ? pick(p('Turn left at the end of the road', 'สุดถนน เลี้ยวซ้าย'))
        : pick(p('Turn right at the end of the road', 'สุดถนน เลี้ยวขวา'));
  }

  switch (mod) {
    case 'uturn':
      return pick(p('Make a U-turn', 'กลับรถ'));
    case 'sharp left':
      return pick(p('Turn sharp left', 'เลี้ยวซ้ายหักศอก'));
    case 'sharp right':
      return pick(p('Turn sharp right', 'เลี้ยวขวาหักศอก'));
    case 'left':
      return pick(p('Turn left', 'เลี้ยวซ้าย'));
    case 'right':
      return pick(p('Turn right', 'เลี้ยวขวา'));
    case 'slight left':
      return pick(p('Bear left', 'เบี่ยงซ้าย'));
    case 'slight right':
      return pick(p('Bear right', 'เบี่ยงขวา'));
    default:
      return pick(p('Continue straight', 'ตรงไป'));
  }
}

/** Speech-friendly distance: rounded to values a driver can act on. */
export function spokenDistance(metres: number, units: UnitSystem, lang: Lang): string {
  if (units === 'imperial') {
    const feet = metres * 3.28084;
    if (feet < 1000) {
      const f = Math.max(100, Math.round(feet / 100) * 100);
      return lang === 'th' ? `${f} ฟุต` : `${f} feet`;
    }
    const miles = metres / 1609.344;
    const m = miles < 1 ? Math.round(miles * 4) / 4 : Math.round(miles * 2) / 2;
    return lang === 'th' ? `${m} ไมล์` : `${m} mile${m === 1 ? '' : 's'}`;
  }

  if (metres < 1000) {
    const step = metres < 300 ? 50 : 100;
    const v = Math.max(50, Math.round(metres / step) * step);
    return lang === 'th' ? `${v} เมตร` : `${v} metres`;
  }
  const km = Math.round((metres / 1000) * 2) / 2;
  return lang === 'th' ? `${km} กิโลเมตร` : `${km} kilometre${km === 1 ? '' : 's'}`;
}

/** Spoken lane warning. The trap case is deliberately blunt. */
export function laneSentence(advice: LaneAdvice | null, lang: Lang): string {
  if (!advice?.changeRequired) return '';
  const n = advice.validCount;

  if (advice.trap === 'left') {
    return lang === 'th' ? 'อย่าอยู่ช่องซ้ายสุด' : 'Do not stay in the left lane';
  }
  if (advice.trap === 'right') {
    return lang === 'th' ? 'อย่าอยู่ช่องขวาสุด' : 'Do not stay in the right lane';
  }

  switch (advice.side) {
    case 'left':
      return lang === 'th'
        ? n === 1
          ? 'ใช้ช่องซ้ายสุด'
          : `ใช้ ${n} ช่องทางซ้าย`
        : n === 1
          ? 'Use the left lane'
          : `Use the ${n} left lanes`;
    case 'right':
      return lang === 'th'
        ? n === 1
          ? 'ใช้ช่องขวาสุด'
          : `ใช้ ${n} ช่องทางขวา`
        : n === 1
          ? 'Use the right lane'
          : `Use the ${n} right lanes`;
    case 'centre':
      return lang === 'th' ? 'ใช้ช่องทางกลาง' : 'Use the middle lanes';
    default:
      return '';
  }
}

/**
 * The one short cue spoken at a fork, and the text of its card strip.
 *
 * Deliberately not built through buildAnnouncement(): a fork is not a turn and
 * must not sound like one. No leading distance ("in 400 metres..."), no
 * follow-on preview - just the side and, when it helps, the road that side
 * keeps you on. "Keep left, stay on 304" is the whole sentence.
 */
export function forkSentence(fork: ForkInfo | null, lang: Lang): string {
  if (!fork) return '';
  const side =
    fork.side === 'left'
      ? lang === 'th'
        ? 'ชิดซ้าย'
        : 'Keep left'
      : lang === 'th'
        ? 'ชิดขวา'
        : 'Keep right';

  const road = roadLabel(fork.step, lang);
  if (!road) return side;

  if (fork.staysOnRoad) {
    return lang === 'th' ? `${side} อยู่บน ${road}` : `${side}, stay on ${road}`;
  }
  return lang === 'th' ? `${side} เข้าสู่ ${road}` : `${side} for ${road}`;
}

/**
 * Where to BE, rather than where not to be.
 *
 * `laneSentence` above leads with the trap ("do not stay in the left lane")
 * because that is the right thing to shout at someone drifting into an
 * exit-only lane. It is the wrong thing to say to a driver sitting in lane
 * three of four when only lane four stays on route: it tells them a lane they
 * are not in is wrong, and nothing about where to go. This always names the
 * target, and says "rightmost" rather than "right" when a single lane out of
 * several is the one that keeps you on the road.
 */
export function laneMoveSentence(advice: LaneAdvice | null, lang: Lang): string {
  if (!advice?.changeRequired) return '';
  const n = advice.validCount;
  const only = n === 1 && advice.total > 2;

  switch (advice.side) {
    case 'left':
      return lang === 'th'
        ? only
          ? 'ใช้ช่องซ้ายสุด'
          : n === 1
            ? 'ใช้ช่องซ้าย'
            : `ใช้ ${n} ช่องทางซ้าย`
        : only
          ? 'Use the leftmost lane'
          : n === 1
            ? 'Use the left lane'
            : `Use the ${n} left lanes`;
    case 'right':
      return lang === 'th'
        ? only
          ? 'ใช้ช่องขวาสุด'
          : n === 1
            ? 'ใช้ช่องขวา'
            : `ใช้ ${n} ช่องทางขวา`
        : only
          ? 'Use the rightmost lane'
          : n === 1
            ? 'Use the right lane'
            : `Use the ${n} right lanes`;
    case 'centre':
      return lang === 'th' ? 'ใช้ช่องทางกลาง' : 'Use the middle lanes';
    default:
      return '';
  }
}

export interface AnnouncementOptions {
  /** The step that BEGINS with the maneuver being announced. */
  maneuverStep: RouteStep;
  /** Metres until the maneuver; 0 or less means "now". */
  distance: number;
  units: UnitSystem;
  lang: Lang;
  advice?: LaneAdvice | null;
  /** Include the lane warning (only worth saying once we're close). */
  withLanes?: boolean;
  /** Follow-on maneuver to preview ("then turn left"). */
  thenStep?: RouteStep | null;
}

/** Compose the full sentence. Every word is in `lang`; no script mixing. */
export function buildAnnouncement(o: AnnouncementOptions): string {
  const { maneuverStep, distance, units, lang } = o;
  const parts: string[] = [];

  const action = verb(maneuverStep, lang);
  const onto = roadLabel(maneuverStep, lang);
  const type = (maneuverStep.maneuver.type ?? '').toLowerCase();
  const wantsOnto = onto && type !== 'arrive' && type !== 'roundabout' && type !== 'rotary';

  const head = wantsOnto
    ? lang === 'th'
      ? `${action} เข้าสู่ ${onto}`
      : `${action} onto ${onto}`
    : action;

  if (distance > 60) {
    const d = spokenDistance(distance, units, lang);
    parts.push(lang === 'th' ? `อีก ${d} ${head}` : `In ${d}, ${head}`);
  } else {
    parts.push(head);
  }

  if (o.withLanes) {
    const lane = laneSentence(o.advice ?? null, lang);
    if (lane) parts.push(lane);
  }

  if (o.thenStep) {
    const next = verb(o.thenStep, lang);
    parts.push(lang === 'th' ? `จากนั้น ${next}` : `Then ${next}`);
  }

  return parts.join('. ') + '.';
}

/** Short label for the road currently being driven, for the sign's top strip. */
export function currentRoadLabel(step: RouteStep | null, lang: Lang): string {
  return roadLabel(step, lang);
}
